import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { ThemeProvider as EmotionThemeProvider, Global, css, } from '@emotion/react';
import { IconContext } from '@phosphor-icons/react';
import merge from 'lodash/merge';
import { theme } from '../theme';
import { ToastProvider } from '../components/toast';
import { ReactDayPickerStyles } from '../styles/vendors';
const fontFamily = css `
  // adds anti-aliasing to retina display devices Chrome and Safari
  // reduces visual font weight
  body {
    font-family: var(--font-work-sans, 'Work Sans'), sans-serif;
    font-feature-settings: 'salt';
  }
  @supports (-webkit-font-smoothing: antialiased) {
    body {
      -webkit-font-smoothing: antialiased;
    }
  }
  // adds anti-aliasing to retina display devices Firefox
  // reduces visual font weight
  @supports (-moz-osx-font-smoothing: grayscale) {
    body {
      -moz-osx-font-smoothing: grayscale;
    }
  }
`;
const saltOn = css `
  * {
    font-feature-settings: 'salt';
  }
`;
const reset = css `
  *,
  *:after,
  *:before {
    box-sizing: border-box;
  }
`;
const defaultIconContext = {
    size: theme.fontSizes.xl,
    weight: 'regular',
    mirrored: false,
};
// TODO: Write test for this
const deepMergeTheme = (baseTheme, customTheme) => merge({}, baseTheme, customTheme);
/**
 * Main provider to populate global styles and inject the theme in your application.
 * Your app needs to wrapped in this provider for ui-uds to function.
 * @returns Component
 */
export const ThemeProvider = ({ children, addFontFamilyToBody = true, selfHostedFonts = false, customTheme = {}, }) => {
    const mergedTheme = useMemo(() => deepMergeTheme(theme, customTheme), [customTheme]);
    const globalFontFace = useMemo(() => {
        if (!selfHostedFonts) {
            return css `
        /* latin-ext */
        @font-face {
          font-family: 'Work Sans';
          font-style: normal;
          font-weight: 300 700;
          font-display: swap;
          src: url('https://app.staging.updater.com/font/WorkSans-VariableFont-LatinSubset-Updater.woff2')
            format('woff2');
          unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB,
            U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
        }
        /* latin */
        @font-face {
          font-family: 'Work Sans';
          font-style: normal;
          font-weight: 300 700;
          font-display: swap;
          src: url('https://app.staging.updater.com/font/WorkSans-VariableFont-LatinSubset-Updater.woff2')
            format('woff2');
          unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6,
            U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193,
            U+2212, U+2215, U+FEFF, U+FFFD;
        }
      `;
        }
        return '';
    }, [selfHostedFonts]);
    return (_jsxs(EmotionThemeProvider, { theme: mergedTheme, children: [_jsx(Global, { styles: css `
          ${reset}
          ${ReactDayPickerStyles}
          ${globalFontFace}
          ${addFontFamilyToBody ? fontFamily : ''}
          ${saltOn}
        ` }), _jsx(IconContext.Provider, { value: defaultIconContext, children: _jsx(ToastProvider, { children: children }) })] }));
};
//# sourceMappingURL=data:application/json;base64,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